import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { AppLayout } from '../components/layouts/AppLayout'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import {
  Package,
  MagnifyingGlass,
  Plus,
  Eye,
  Pencil,
  Trash,
  CheckSquare,
  Square,
  DownloadSimple,
  Boat,
  Airplane,
  Truck,
  ArrowRight,
  Clock,
  Play,
  XCircle,
  ChatCircleDots,
  ArrowsClockwise,
} from '@phosphor-icons/react'
import { listQuotes, deleteQuote, activateQuote, cancelQuote, duplicateQuote } from '../services/quotes'
import { Quote, QUOTE_STATUS_LABELS, MODALITY_LABELS } from '../types/quote'
import { useDialog } from '../context/DialogContext'
import { ConfirmDialog } from '../components/ui/ConfirmDialog'
import {
  getActivityIndicator,
  getInitials,
  formatDateBR,
  typography,
  spacing,
  components,
} from '../styles/design-system'

export default function QuotesPage() {
  const [quotes, setQuotes] = useState<Quote[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  // Default view shows live quotes receiving offers (active + in_progress are
  // merged into one "Recebendo propostas" status). Drafts, concluded and
  // canceled quotes are hidden until explicitly filtered.
  // A ?status= query (e.g. from the dashboard informe, Plane #23) overrides it.
  const [searchParams] = useSearchParams()
  const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || 'active')
  const [filterModality, setFilterModality] = useState('all')
  const [selectedQuotes, setSelectedQuotes] = useState<string[]>([])
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const { showSuccess, showError, confirm } = useDialog()

  // Dialog states
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  const [cancelReason, setCancelReason] = useState('')
  const [cancelLoading, setCancelLoading] = useState(false)
  const [selectedQuoteId, setSelectedQuoteId] = useState<string | null>(null)

  // Reload when crossing the deleted boundary (deleted quotes need a separate
  // fetch); other status filters are applied locally.
  useEffect(() => {
    loadQuotes()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus === 'deleted'])

  async function loadQuotes() {
    try {
      setLoading(true)
      // Normalmente carrega todas (filtro local). Para "Excluída" o backend
      // precisa de status=deleted (is_deleted=true), que não vem em "all".
      const data = await listQuotes({ status: (filterStatus === 'deleted' ? 'deleted' : 'all') as any })
      setQuotes(data)
    } catch (error: any) {
      console.error('Erro ao carregar cotações:', error)
      showError('Erro', error.response?.data?.error || 'Erro ao carregar cotações')
    } finally {
      setLoading(false)
    }
  }

  async function handleActivate(id: string) {
    const confirmed = await confirm({
      title: 'Ativar Cotação',
      message: 'Ativar esta cotação? Ela ficará visível para os freight forwarders.',
      variant: 'success',
      confirmText: 'Sim, Ativar',
      cancelText: 'Cancelar'
    })

    if (!confirmed) return

    try {
      await activateQuote(id)
      showSuccess('Cotação Ativada', 'A cotação foi ativada com sucesso!')
      loadQuotes()
    } catch (error: any) {
      showError('Erro', error.response?.data?.error || 'Erro ao ativar cotação')
    }
  }

  function openCancelDialog(id: string) {
    setSelectedQuoteId(id)
    setCancelReason('')
    setCancelDialogOpen(true)
  }

  async function handleCancelConfirm() {
    if (!selectedQuoteId) return

    try {
      setCancelLoading(true)
      await cancelQuote(selectedQuoteId, cancelReason || undefined)
      setCancelDialogOpen(false)
      showSuccess('Cotação Cancelada', 'A cotação foi cancelada com sucesso.')
      loadQuotes()
    } catch (error: any) {
      showError('Erro', error.response?.data?.error || 'Erro ao cancelar cotação')
    } finally {
      setCancelLoading(false)
      setSelectedQuoteId(null)
    }
  }

  async function handleDelete(id: string) {
    const confirmed = await confirm({
      title: 'Remover Cotação',
      message: 'Tem certeza que deseja remover esta cotação? Esta ação não pode ser desfeita.',
      variant: 'danger',
      confirmText: 'Sim, Remover',
      cancelText: 'Cancelar'
    })

    if (!confirmed) return

    try {
      await deleteQuote(id)
      showSuccess('Cotação Removida', 'A cotação foi removida com sucesso.')
      loadQuotes()
    } catch (error: any) {
      showError('Erro', error.response?.data?.error || 'Erro ao remover cotação')
    }
  }

  async function handleDuplicate(id: string) {
    const confirmed = await confirm({
      title: 'Recriar Cotação',
      message: 'Criar uma nova cotação com os mesmos dados? A nova cotação ficará ativa e disponível para receber ofertas.',
      variant: 'success',
      confirmText: 'Sim, Recriar',
      cancelText: 'Cancelar'
    })

    if (!confirmed) return

    try {
      const newQuote = await duplicateQuote(id)
      showSuccess('Cotação Recriada', `Nova cotação ${newQuote.tx_control} criada com sucesso!`)
      loadQuotes()
    } catch (error: any) {
      showError('Erro', error.response?.data?.error || 'Erro ao recriar cotação')
    }
  }

  // Filter functionality
  const filteredQuotes = quotes.filter((quote) => {
    const matchesSearch =
      quote.tx_control.toLowerCase().includes(searchQuery.toLowerCase()) ||
      quote.goods_description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      quote.client_reference?.toLowerCase().includes(searchQuery.toLowerCase())

    const matchesStatus =
      filterStatus === 'all' || filterStatus === 'deleted'
        ? true // 'deleted' already fetched the excluded set from the backend
        : filterStatus === 'active'
          ? ['active', 'in_progress'].includes(quote.status) // merged "Recebendo propostas"
          : quote.status === filterStatus
    const matchesModality = filterModality === 'all' || quote.modality_name === filterModality

    return matchesSearch && matchesStatus && matchesModality
  })

  // Selection handlers
  const toggleSelectAll = () => {
    if (selectedQuotes.length === filteredQuotes.length) {
      setSelectedQuotes([])
    } else {
      setSelectedQuotes(filteredQuotes.map((q) => q.id))
    }
  }

  const toggleSelectQuote = (quoteId: string) => {
    setSelectedQuotes((prev) =>
      prev.includes(quoteId)
        ? prev.filter((id) => id !== quoteId)
        : [...prev, quoteId]
    )
  }

  const isQuoteSelected = (quoteId: string) => selectedQuotes.includes(quoteId)
  const isAllSelected =
    filteredQuotes.length > 0 && selectedQuotes.length === filteredQuotes.length

  // Get status badge classes
  const getStatusBadgeClasses = (status: string): string => {
    const classes = {
      active: 'bg-emerald-100 text-emerald-700',
      in_progress: 'bg-emerald-100 text-emerald-700',
      draft: 'bg-amber-100 text-amber-700',
      completed: 'bg-blue-100 text-blue-700',
      canceled: 'bg-red-100 text-red-700',
    }
    return `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
      classes[status as keyof typeof classes] || 'bg-gray-100 text-gray-700'
    }`
  }

  // Get modality badge
  const getModalityBadge = (modality: string) => {
    const badges = {
      maritimo: { icon: <Boat size={12} />, color: 'text-blue-600', label: 'Marítimo' },
      aereo: { icon: <Airplane size={12} />, color: 'text-purple-600', label: 'Aéreo' },
      terrestre: { icon: <Truck size={12} />, color: 'text-green-600', label: 'Terrestre' },
    }
    const badge = badges[modality as keyof typeof badges]
    if (!badge) return null

    return (
      <span className={`inline-flex items-center gap-1 text-xs font-medium ${badge.color}`}>
        {badge.icon}
        {badge.label}
      </span>
    )
  }

  // Calculate metrics
  const metrics = {
    overview: {
      total: quotes.length,
      active: quotes.filter((q) => q.status === 'active' || q.status === 'in_progress').length,
      draft: quotes.filter((q) => q.status === 'draft').length,
    },
    byModality: {
      maritime: quotes.filter((q) => q.modality_name === 'maritimo').length,
      air: quotes.filter((q) => q.modality_name === 'aereo').length,
      ground: quotes.filter((q) => q.modality_name === 'terrestre').length,
    },
    activity: {
      thisWeek: quotes.filter(
        (q) =>
          new Date(q.created_at).getTime() >
          new Date().getTime() - 7 * 24 * 60 * 60 * 1000
      ).length,
      completed: quotes.filter((q) => q.status === 'completed').length,
      canceled: quotes.filter((q) => q.status === 'canceled').length,
    },
  }

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-screen">
          <p className="text-gray-500">Carregando...</p>
        </div>
      </AppLayout>
    )
  }

  return (
    <AppLayout>
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Minhas Cotações</h1>
            </div>
            <div className="flex items-center gap-3">
              {selectedQuotes.length > 0 && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <span className="font-medium">{selectedQuotes.length}</span>
                  selecionada{selectedQuotes.length > 1 ? 's' : ''}
                </div>
              )}
              <Button
                onClick={() => navigate('/quotes/new')}
                size="default"
                className="gap-2 bg-emerald-600 hover:bg-emerald-700"
              >
                <Plus size={18} weight="bold" />
                Nova Cotação
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Metrics Zone - 3 Column Grid */}
        <div className="grid grid-cols-3 gap-6 mb-6">
          {/* Overview Card */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
              Visão Geral
            </p>
            <div className="flex items-end justify-between">
              <div>
                <p className="text-3xl font-bold text-gray-900">{metrics.overview.total}</p>
                <p className="text-sm text-gray-600 mt-1">
                  {metrics.overview.active} ativas, {metrics.overview.draft} rascunhos
                </p>
              </div>
              <Package size={32} className="text-emerald-600 opacity-80" weight="duotone" />
            </div>
          </div>

          {/* Modalities Card */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
              Por Modalidade
            </p>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 flex items-center gap-2">
                  <Boat size={14} className="text-blue-600" />
                  Marítimo
                </span>
                <span className="font-semibold text-gray-900">{metrics.byModality.maritime}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 flex items-center gap-2">
                  <Airplane size={14} className="text-purple-600" />
                  Aéreo
                </span>
                <span className="font-semibold text-gray-900">{metrics.byModality.air}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 flex items-center gap-2">
                  <Truck size={14} className="text-green-600" />
                  Terrestre
                </span>
                <span className="font-semibold text-gray-900">{metrics.byModality.ground}</span>
              </div>
            </div>
          </div>

          {/* Activity Card */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
              Atividade
            </p>
            <div className="flex items-end justify-between">
              <div>
                <p className="text-3xl font-bold text-emerald-600">{metrics.activity.thisWeek}</p>
                <p className="text-sm text-gray-600 mt-1">Esta semana</p>
              </div>
              <div className="text-right text-sm text-gray-600">
                <div className="mb-1">
                  <span className="font-medium text-gray-900">{metrics.activity.completed}</span>{' '}
                  aprovadas
                </div>
                <div>
                  <span className="font-medium text-gray-900">{metrics.activity.canceled}</span>{' '}
                  canceladas
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filter Bar - Horizontal Compact */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-3">
            {/* Search */}
            <div className="flex-1 relative">
              <MagnifyingGlass
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <Input
                type="text"
                placeholder="Buscar por número ou descrição..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-9 border-gray-200"
              />
            </div>

            {/* Status Filter */}
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 h-9 border border-gray-200 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="active">Recebendo propostas</option>
              <option value="draft">Rascunho</option>
              <option value="completed">Aprovada</option>
              <option value="canceled">Cancelada</option>
              <option value="deleted">Excluída</option>
              <option value="all">Todos Status</option>
            </select>

            {/* Modality Filter */}
            <select
              value={filterModality}
              onChange={(e) => setFilterModality(e.target.value)}
              className="px-3 h-9 border border-gray-200 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="all">Todas Modalidades</option>
              <option value="maritimo">Marítimo</option>
              <option value="aereo">Aéreo</option>
              <option value="terrestre">Terrestre</option>
            </select>

            {/* Export */}
            <Button variant="outline" size="sm" className="gap-2 h-9">
              <DownloadSimple size={16} />
              Exportar
            </Button>
          </div>
        </div>

        {/* Data Grid */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="w-12 px-4 py-3">
                    <button
                      onClick={toggleSelectAll}
                      className="flex items-center justify-center w-full"
                    >
                      {isAllSelected ? (
                        <CheckSquare size={18} weight="fill" className="text-emerald-600" />
                      ) : (
                        <Square size={18} className="text-gray-400" />
                      )}
                    </button>
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cotação
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rota
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Modalidade
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Incoterm
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ofertas
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Criada em
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredQuotes.map((quote) => (
                  <tr
                    key={quote.id}
                    className={`hover:bg-gray-50 transition-colors ${
                      isQuoteSelected(quote.id) ? 'bg-blue-50' : ''
                    }`}
                  >
                    <td className="px-4 py-3">
                      <button
                        onClick={() => toggleSelectQuote(quote.id)}
                        className="flex items-center justify-center"
                      >
                        {isQuoteSelected(quote.id) ? (
                          <CheckSquare size={18} weight="fill" className="text-emerald-600" />
                        ) : (
                          <Square size={18} className="text-gray-400" />
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div {...getActivityIndicator(quote.created_at)} />
                        <div className="min-w-0 max-w-[320px]">
                          <p className="font-medium text-sm text-gray-900 truncate">
                            {quote.tx_control}
                          </p>
                          {quote.client_reference && (
                            <p className="text-xs text-emerald-600 truncate" title={quote.client_reference}>
                              Ref: {quote.client_reference}
                            </p>
                          )}
                          {quote.goods_description && (
                            <p className="text-xs text-gray-500 truncate" title={quote.goods_description}>
                              {quote.goods_description}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <span className="truncate max-w-[100px]">
                          {quote.origin.location.city}, {quote.origin.location.country}
                        </span>
                        <ArrowRight size={14} className="text-gray-400 flex-shrink-0" />
                        <span className="truncate max-w-[100px]">
                          {quote.destination.location.city}, {quote.destination.location.country}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {getModalityBadge(quote.modality_name || '')}
                      {quote.volume_name && (
                        <p className="text-xs text-gray-500 mt-1">{quote.volume_name}</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-medium text-gray-900">{quote.incoterm}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={getStatusBadgeClasses(quote.status)}>
                        {QUOTE_STATUS_LABELS[quote.status]}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {(quote.offers_count ?? 0) > 0 ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            navigate(`/shipper/quotes/${quote.id}?tab=offers`)
                          }}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors"
                        >
                          <ChatCircleDots size={14} weight="fill" />
                          {quote.offers_count} {quote.offers_count === 1 ? 'oferta' : 'ofertas'}
                        </button>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600">{formatDateBR(quote.created_at)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/quotes/${quote.id}`)}
                          className="h-9 w-9 p-0"
                          title="Ver detalhes"
                        >
                          <Eye size={20} className="text-gray-600" />
                        </Button>
                        {(quote.status === 'draft' || quote.status === 'active') && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/quotes/${quote.id}/edit`)}
                              className="h-9 w-9 p-0"
                              title="Editar cotação"
                            >
                              <Pencil size={20} className="text-gray-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(quote.id)}
                              className="h-9 w-9 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                              title="Excluir cotação"
                            >
                              <Trash size={20} />
                            </Button>
                          </>
                        )}
                        {(quote.status === 'completed' || quote.status === 'canceled') && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDuplicate(quote.id)}
                            className="h-9 w-9 p-0 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                            title="Recriar cotação"
                          >
                            <ArrowsClockwise size={20} />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredQuotes.length === 0 && (
            <div className="text-center py-12 bg-gray-50">
              <Package size={48} className="mx-auto text-gray-300 mb-3" weight="duotone" />
              <p className="text-gray-500 text-sm">Nenhuma cotação encontrada</p>
            </div>
          )}

          {/* Footer with pagination */}
          <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              <span className="font-medium">{filteredQuotes.length}</span> cotações
              {selectedQuotes.length > 0 && (
                <>
                  {' '}
                  · <span className="font-medium">{selectedQuotes.length}</span> selecionada
                  {selectedQuotes.length > 1 ? 's' : ''}
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              <select className="px-2 py-1 border border-gray-200 rounded text-sm bg-white">
                <option>50 por página</option>
                <option>100 por página</option>
                <option>200 por página</option>
              </select>
              <span className="text-sm text-gray-600">Página 1 de 1</span>
            </div>
          </div>
        </div>
      </main>

      {/* Cancel Quote Dialog */}
      <ConfirmDialog
        isOpen={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        onConfirm={handleCancelConfirm}
        title="Cancelar Cotação"
        message={
          <div className="space-y-4">
            <p>Tem certeza que deseja cancelar esta cotação? Os agentes de carga não poderão mais enviar propostas.</p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Motivo do cancelamento (opcional)
              </label>
              <Input
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Ex: Mudança de estratégia, projeto cancelado..."
              />
            </div>
          </div>
        }
        variant="warning"
        confirmText="Sim, Cancelar"
        cancelText="Voltar"
        isLoading={cancelLoading}
        icon={<XCircle size={32} weight="duotone" />}
      />
    </AppLayout>
  )
}
